<template>
	<v-dialog
		:value="true"
		:fullscreen="$vuetify.breakpoint.mobile"
		max-width="640"
		content-class="fsm-dialog"
		@input="$emit('close')"
		@click:outside="$emit('close')"
		@keydown.esc="$emit('close')"
	>
		<div class="fsm-root">
			<div class="fsm-header">
				<div class="fsm-header__title">
					<v-icon size="18" color="#2D4B95" class="mr-2">mdi-folder-search-outline</v-icon>
					<span>Seleccionar archivo</span>
				</div>
				<button class="icon-action-btn" @click="$emit('close')">
					<v-icon size="16" color="#576080">mdi-close</v-icon>
				</button>
			</div>

			<div class="fsm-search">
				<v-text-field
					v-model="searchQuery"
					label="Buscar archivo por nombre"
					outlined
					hide-details
					dense
					clearable
					class="custom-text-field-minify fsm-search-field"
					@input="onSearchInput"
					@click:clear="onClearSearch"
				>
					<template slot="prepend-inner">
						<v-icon small color="#5e85ef" class="mr-1">mdi-magnify</v-icon>
					</template>
					<template slot="append">
						<v-progress-circular
							v-if="loading"
							:size="16"
							:width="2"
							color="#5e85ef"
							indeterminate
						></v-progress-circular>
					</template>
				</v-text-field>
			</div>

			<div class="fsm-breadcrumb" v-if="!isSearching">
				<span class="fsm-crumb" :class="{ 'fsm-crumb--current': folderStack.length === 0 }" @click="goToBreadcrumb(-1)">
					<v-icon size="14" class="mr-1">mdi-home-outline</v-icon>Inicio
				</span>
				<div v-for="(folder, index) in folderStack" :key="folder.uid" class="fsm-crumb-group">
					<v-icon size="14" class="fsm-crumb-sep">mdi-chevron-right</v-icon>
					<span
						class="fsm-crumb"
						:class="{ 'fsm-crumb--current': index === folderStack.length - 1 }"
						@click="goToBreadcrumb(index)"
					>{{ folder.name }}</span>
				</div>
			</div>
			<div class="fsm-breadcrumb fsm-breadcrumb--search" v-else>
				<v-icon size="14" class="mr-1" color="#5e85ef">mdi-magnify</v-icon>
				Resultados para "{{ searchQuery }}"
			</div>

			<div class="fsm-body">
				<div v-if="loading" class="fsm-state">
					<v-progress-circular indeterminate color="#5e85ef" :size="28" :width="3" />
				</div>

				<div v-else-if="displayItems.length === 0" class="fsm-state">
					<v-icon size="34" color="#c2cbe0">{{ isSearching ? 'mdi-file-search-outline' : 'mdi-folder-open-outline' }}</v-icon>
					<span class="fsm-state__text">
						{{ isSearching ? 'No se encontraron archivos para tu búsqueda' : 'Esta carpeta está vacía' }}
					</span>
				</div>

				<div v-else class="fsm-list">
					<div
						v-for="item in displayItems"
						:key="item.uid"
						class="fsm-row"
						:class="{
							'fsm-row--folder': item.type === 'folder',
							'fsm-row--disabled': isFileDisabled(item)
						}"
						@click="handleRowClick(item)"
					>
						<v-icon
							size="20"
							:color="item.type === 'folder' ? '#5e85ef' : (isFileDisabled(item) ? '#c2cbe0' : '#8b94ad')"
							class="mr-2"
						>
							{{ item.type === 'folder' ? 'mdi-folder' : fileIcon(item.ext) }}
						</v-icon>
						<span class="fsm-row__name text-truncate">{{ item.name }}</span>
						<v-icon
							v-if="item.type !== 'folder'"
							size="18"
							:color="isFileDisabled(item) ? '#c2cbe0' : '#5e85ef'"
							class="fsm-row__select"
						>{{ isFileDisabled(item) ? 'mdi-lock-outline' : 'mdi-check-circle-outline' }}</v-icon>
						<v-icon
							v-else
							size="18"
							color="#a0aec0"
							class="fsm-row__arrow"
						>mdi-chevron-right</v-icon>
					</div>
				</div>
			</div>
		</div>
	</v-dialog>
</template>

<script>
import _debounce from 'lodash/debounce'
import { getTreePath } from '@/helpers/api/files'

export default {
	name: 'FileSelectorModal',
	props: {
		// Optional predicate used by consumers that need to restrict selectable files.
		// When omitted, every non-folder keeps the original selectable behavior.
		fileFilter: {
			type: Function,
			default: null
		}
	},
	data: () => ({
		items: [],
		folderStack: [],
		searchQuery: '',
		loading: false,
	}),
	computed: {
		selectedProject() {
			return this.$store.projectsStore.state.selectedProject
		},
		currentFolderId() {
			return this.folderStack.length > 0 ? this.folderStack[this.folderStack.length - 1].id : null
		},
		isSearching() {
			return !!(this.searchQuery && this.searchQuery.trim().length > 0)
		},
		displayItems() {
			if (this.isSearching) {
				return this.items.filter(item => item.type !== 'folder')
			}
			return this.items
		}
	},
	mounted() {
		this.fetchItems()
	},
	methods: {
		fileIcon(ext) {
			const e = (ext || '').toLowerCase()
			if (['pdf'].includes(e)) return 'mdi-file-pdf-box'
			if (['doc', 'docx'].includes(e)) return 'mdi-file-word-box'
			if (['xls', 'xlsx'].includes(e)) return 'mdi-file-excel-box'
			if (['ppt', 'pptx'].includes(e)) return 'mdi-file-powerpoint-box'
			if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return 'mdi-file-image-box'
			if (['zip', 'rar', '7z'].includes(e)) return 'mdi-folder-zip-outline'
			return 'mdi-file-outline'
		},
		isFileDisabled(item) {
			return item.type !== 'folder' && this.fileFilter && !this.fileFilter(item)
		},
		onSearchInput: _debounce(function () {
			this.fetchItems()
		}, 500),
		onClearSearch() {
			this.searchQuery = ''
			this.fetchItems()
		},
		async fetchItems() {
			if (!this.selectedProject) return
			this.loading = true
			const data = { project_id: this.selectedProject.id }
			if (this.isSearching) {
				data.search = this.searchQuery.trim()
			} else {
				data.parent_id = this.currentFolderId
			}
			try {
				const res = await getTreePath(data)
				if (res.code === 200) {
					this.items = res.data
				}
			} finally {
				this.loading = false
			}
		},
		handleRowClick(item) {
			if (item.type === 'folder') {
				this.openFolder(item)
			} else if (!this.isFileDisabled(item)) {
				this.selectFile(item)
			}
		},
		openFolder(item) {
			this.folderStack.push({ uid: item.uid, id: item.id, name: item.name })
			this.searchQuery = ''
			this.fetchItems()
		},
		goToBreadcrumb(index) {
			this.folderStack = index === -1 ? [] : this.folderStack.slice(0, index + 1)
			this.searchQuery = ''
			this.fetchItems()
		},
		selectFile(item) {
			this.$emit('select', item)
		},
	}
}
</script>

<style scoped>
.fsm-root {
	background: #ffffff;
	border-radius: 10px;
	display: flex;
	flex-direction: column;
	max-height: 78vh;
	font-family: inherit;
}

.fsm-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 16px;
	border-bottom: 1px solid rgba(45, 75, 149, 0.09);
}

.fsm-header__title {
	display: flex;
	align-items: center;
	font-size: 14px;
	font-weight: 700;
	color: #2d3a5c;
}

.icon-action-btn {
	width: 28px; height: 28px; border-radius: 7px;
	background: #EEF2FF; border: none; cursor: pointer;
	display: inline-flex; align-items: center; justify-content: center;
	transition: background 0.12s;
}
.icon-action-btn:hover { background: #dde5f9; }

.fsm-search {
	padding: 12px 16px 4px;
}
.fsm-search-field /deep/ .v-input__slot {
	border-radius: 8px !important;
	height: 36px !important;
	min-height: 36px !important;
	display: flex !important;
	align-items: center !important;
}

.fsm-breadcrumb {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 2px;
	padding: 8px 16px;
	font-size: 12px;
	color: #576080;
}
.fsm-breadcrumb--search {
	color: #5e85ef;
	font-weight: 600;
}
.fsm-crumb {
	display: inline-flex;
	align-items: center;
	cursor: pointer;
	padding: 2px 4px;
	border-radius: 4px;
	color: #576080;
}
.fsm-crumb:hover { background: #f0f4ff; color: #2D4B95; }
.fsm-crumb--current { color: #2D4B95; font-weight: 700; }
.fsm-crumb-sep { color: #c2cbe0; margin: 0 1px; }
.fsm-crumb-group { display: contents; }

.fsm-body {
	overflow-y: auto;
	flex: 1;
	min-height: 220px;
	padding: 4px 8px 12px;
}

.fsm-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 48px 16px;
	color: #99a5be;
}
.fsm-state__text {
	font-size: 12.5px;
	text-align: center;
	max-width: 280px;
}

.fsm-list {
	display: flex;
	flex-direction: column;
}

.fsm-row {
	display: flex;
	align-items: center;
	padding: 9px 10px;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.12s;
}
.fsm-row:hover {
	background: #f0f4ff;
}
.fsm-row--disabled {
	cursor: not-allowed;
	opacity: 0.62;
}
.fsm-row--disabled:hover {
	background: transparent;
}
.fsm-row__name {
	flex: 1;
	font-size: 13px;
	color: #2d3a5c;
}
.fsm-row--folder .fsm-row__name {
	font-weight: 600;
}
.fsm-row--disabled .fsm-row__name {
	color: #a0aec0;
}
.fsm-row__select {
	opacity: 0;
	transition: opacity 0.12s;
}
.fsm-row:hover .fsm-row__select {
	opacity: 1;
}
.fsm-row__arrow {
	opacity: 0.7;
}
</style>